// src/App.jsx

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login          from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import StudentPortal  from "./pages/StudentPortal";
import EnrollStudent  from "./pages/EnrollStudent";
import StudentKiosk   from "./pages/StudentKiosk";
import LifeAI         from "./pages/LifeAI";
import Reports        from "./pages/Reports";
import CompleteProfile from "./pages/CompleteProfile";
import LogTimelineReport from "./pages/LogTimelineReport";
import DietAnalysis from "./pages/DietAnalysis";
import IssueCertificate from "./pages/IssueCertificate";
import ManageCertificates from "./pages/ManageCertificates";
import VerifyCertificate from "./pages/VerifyCertificate";
// LMS Pages
import CourseList from "./pages/CourseList";
import AddCourse from "./pages/AddCourse";
import ManageLessons from "./pages/ManageLessons";
import AssignStudents from "./pages/AssignStudents";
import AdminCourseProgress from "./pages/AdminCourseProgress";
import EditCourse from "./pages/EditCourse";
// Accounting Pages
import CustomerList from "./pages/CustomerList";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import InvoiceList from "./pages/InvoiceList";
import CreateInvoice from "./pages/CreateInvoice";
import ItemMaster from "./pages/ItemMaster";
import EditInvoice from "./pages/EditInvoice";
import ExpenseList from "./pages/ExpenseList";
import JournalVoucher from "./pages/JournalVoucher";
import FinancialReports from "./pages/FinancialReports";
import DashboardLayout from "./components/DashboardLayout";
import TypingTutor from "./pages/TypingTutor";
import TypingTutorSettings from "./pages/TypingTutorSettings";
import TypingProgress from "./pages/TypingProgress";
import SkillsManagement from "./pages/SkillsManagement";
import CRM from "./pages/CRM";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, role, ready } = useAuth();
  if (!ready) return <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"DM Sans,sans-serif",color:"#6B7280" }}>Loading…</div>;
  if (!user)  return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, role, ready } = useAuth();

  if (!ready) return <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans" }}>Loading application...</div>;

  // Logic to determine where a logged-in user should be by default
  const defaultPath = (role === "student" || role === "staff_limited") ? "/student" : role === "kiosk" ? "/kiosk" : "/admin";

  return (
    <Routes>
      {/* ── Public Landing ── */}
      <Route path="/" element={<LifeAI />} />

      {/* Auth */}
      <Route path="/login" element={user ? <Navigate to={defaultPath} replace /> : <Login />} />

      {/* ── Student Portal ── */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="overview" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/courses" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="courses" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/self-log" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="self-log" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/log-report" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="log-report" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/diet-report" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="diet-report" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/ai-assistant" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="ai-assistant" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/diary" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="diary" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/password" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="password" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/profile" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="profile" /></DashboardLayout>
        </ProtectedRoute>
      } />


      {/* ── Kiosk ── */}
      <Route path="/kiosk" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "kiosk"]}>
          <StudentKiosk />
        </ProtectedRoute>
      } />

      {/* ── Admin / Trainer Dashboard ── */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="overview" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/people" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="people" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/progress" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="progress" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/self-progress" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="self-progress" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/whatsapp" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><AdminDashboard view="whatsapp" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="messages" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-chat" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="ai-chat" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/kiosks" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><AdminDashboard view="kiosks" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/kiosk" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminDashboard view="kiosk" /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><AdminDashboard view="settings" /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/typing-tutor" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited", "instructor", "admin", "trainer", "kiosk"]}>
          <DashboardLayout><TypingTutor /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Typing Tutor Settings (Admin/Instructor Only) ── */}
      <Route path="/admin/typing-settings" element={
        <ProtectedRoute allowedRoles={["admin", "instructor"]}>
          <DashboardLayout><TypingTutorSettings /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/typing-progress" element={
        <ProtectedRoute allowedRoles={["admin", "instructor"]}>
          <DashboardLayout><TypingProgress /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/skills" element={
        <ProtectedRoute allowedRoles={["admin", "instructor"]}>
          <DashboardLayout><SkillsManagement /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/crm" element={
        <ProtectedRoute allowedRoles={["admin", "instructor", "trainer"]}>
          <DashboardLayout><CRM /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/student/crm" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><CRM /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/student/skills" element={
        <ProtectedRoute allowedRoles={["student", "staff_limited"]}>
          <DashboardLayout><StudentPortal view="skills" /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Enroll student — admin + trainer + instructor */}
      <Route path="/enroll" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><EnrollStudent /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Re-enroll / Update face — admin + trainer */}
      <Route path="/re-enroll/:id" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><EnrollStudent isUpdate={true} /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Reports (admin + trainer + instructor) */}
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><Reports /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/log-report" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><LogTimelineReport /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/diet-report" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><DietAnalysis /></DashboardLayout>
        </ProtectedRoute>
      } />


      <Route path="/complete-profile/:id" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor", "kiosk"]}>
          <CompleteProfile />
        </ProtectedRoute>
      } />

      {/* ── LMS: Course Management ── */}
      <Route path="/admin/courses" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><CourseList /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/courses/add" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AddCourse /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/courses/:courseId/edit" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><EditCourse /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/courses/:courseId/lessons" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><ManageLessons /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/courses/:courseId/assign" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AssignStudents /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/courses/:courseId/progress" element={
        <ProtectedRoute allowedRoles={["admin", "trainer", "instructor"]}>
          <DashboardLayout><AdminCourseProgress /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Customers ── */}
      <Route path="/admin/customers" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><CustomerList /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Chart of Accounts ── */}
      <Route path="/admin/accounts" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><ChartOfAccounts /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Invoices ── */}
      <Route path="/admin/invoices" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><InvoiceList /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/invoices/create" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><CreateInvoice /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/invoices/:invoiceId" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><EditInvoice /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Item Master ── */}
      <Route path="/admin/items" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><ItemMaster /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Expenses ── */}
      <Route path="/admin/expenses" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><ExpenseList /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Journal Voucher ── */}
      <Route path="/admin/jv" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><JournalVoucher /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Accounting: Financial Reports ── */}
      <Route path="/admin/financial-reports" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><FinancialReports /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ── Certificate Management ── */}
      <Route path="/admin/certificates" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><ManageCertificates /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/issue-certificate" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><IssueCertificate /></DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/issue-certificate/:certId" element={
        <ProtectedRoute allowedRoles={["admin", "trainer"]}>
          <DashboardLayout><IssueCertificate /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Public Verification Link */}
      <Route path="/verify/:certId" element={<VerifyCertificate />} />

      <Route path="*" element={<Navigate to={user ? defaultPath : "/login"} replace />} />
    </Routes>
  );
}


export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
